/**
 * bak-color-thumbs.js
 *
 * Renders a row of color thumbnails inside the quick-add drawer.
 * Data source: <script class="bak-color-variants-data"> JSON embedded
 * in each <quick-add-component> by snippets/quick-add.liquid.
 *
 * Click swaps the product in-modal via the existing
 * QuickAddComponent.fetchProductPage + .updateQuickAddModal, then
 * re-renders the thumbs row with the new active product highlighted.
 */
(function () {
  'use strict';

  function findActiveQuickAdd() {
    const lastTriggered = document.querySelector('quick-add-component[data-bak-last-triggered="true"]');
    if (lastTriggered) return lastTriggered;
    return document.querySelector('quick-add-component');
  }

  function readVariants(quickAdd) {
    try {
      const variantsScript = quickAdd.querySelector('script.bak-color-variants-data');
      const currentScript = quickAdd.querySelector('script.bak-current-product-data');
      if (!currentScript) return null; // at least current product is required
      const variants = variantsScript ? JSON.parse(variantsScript.textContent || '[]') : [];
      const current = JSON.parse(currentScript.textContent || '{}');
      const seen = new Set();
      const all = [current, ...variants].filter((p) => {
        if (!p || !p.handle) return false;
        if (seen.has(p.handle)) return false;
        seen.add(p.handle);
        return true;
      });
      return { current, all };
    } catch (e) {
      console.warn('[bak-color-thumbs] failed to parse variants data', e);
      return null;
    }
  }

  function buildRow(data) {
    const wrap = document.createElement('div');
    wrap.className = 'bak-color-thumbs';

    const row = document.createElement('div');
    row.className = 'bak-color-thumbs__row';
    row.innerHTML = data.all
      .map((p) => {
        const isActive = p.handle === data.current.handle;
        const safeTitle = (p.title || '').replace(/"/g, '&quot;');
        const safeUrl = (p.url || '').replace(/"/g, '&quot;');
        const safeImage = (p.image || '').replace(/"/g, '&quot;');
        return `
          <button type="button"
                  class="bak-color-thumb${isActive ? ' is-active' : ''}"
                  data-product-handle="${p.handle}"
                  data-product-url="${safeUrl}"
                  aria-label="${safeTitle}"
                  title="${safeTitle}">
            <img src="${safeImage}" alt="${safeTitle}" loading="lazy">
          </button>`;
      })
      .join('');
    wrap.appendChild(row);

    // Active color name below the thumbs (lowercase, like the PDP swatch label)
    const currentColorName = (data.current && data.current.color_name) || '';
    if (currentColorName) {
      const name = document.createElement('div');
      name.className = 'bak-color-thumbs__name';
      name.textContent = String(currentColorName).toLowerCase();
      wrap.appendChild(name);
    }
    return wrap;
  }

  function renderProductLink(modal) {
    if (!modal || !modal.hasAttribute('open')) return;
    const productDetails = modal.querySelector('.product-details');
    const modalContent = modal.querySelector('#quick-add-modal-content');
    const container = productDetails || modalContent;
    if (!container) return;

    // Pull the product URL — mehrere Fallbacks da der Title-Link in Horizon
    // unregelmäßig vorhanden ist (mobile/desktop, manche Produkte rendern keinen <a>).
    let productUrl = null;
    // 1) Title-Link
    const titleLink = container.querySelector('.view-product-title a, .product-header a, [class*="product-title"] a, h1 a');
    if (titleLink && titleLink.getAttribute('href')) productUrl = titleLink.getAttribute('href');
    // 2) Modal-Attribut data-bak-current-handle
    if (!productUrl) {
      const dialog = modal;
      const handle = dialog && (dialog.getAttribute('data-bak-current-handle') || dialog.dataset.bakCurrentHandle);
      if (handle) productUrl = '/products/' + handle;
    }
    // 3) variant-picker oder form action /products/.../variants
    if (!productUrl) {
      const vp = container.querySelector('variant-picker[data-product-url]');
      if (vp) productUrl = vp.getAttribute('data-product-url');
    }
    // 4) jeder /products/ Link im Container (z.B. add-to-cart form action)
    if (!productUrl) {
      const anyProdLink = container.querySelector('a[href*="/products/"]');
      if (anyProdLink) {
        const href = anyProdLink.getAttribute('href');
        // entferne Query-Strings, behalte nur /products/handle Pfad
        const m = href && href.match(/(\/products\/[^?#]+)/);
        if (m) productUrl = m[1];
      }
    }
    // 5) Hotspot oder data-handle Attribute am Modal-Wrapper
    if (!productUrl) {
      const handleEl = container.querySelector('[data-product-handle]');
      if (handleEl) {
        const h = handleEl.getAttribute('data-product-handle');
        if (h) productUrl = '/products/' + h;
      }
    }
    if (!productUrl) return;

    // Idempotent: remove existing before re-adding
    const existing = container.querySelector('.bak-pdp-link');
    if (existing) existing.remove();

    const link = document.createElement('a');
    link.className = 'bak-pdp-link';
    link.href = productUrl;
    link.textContent = (((document.documentElement.lang || (window.Shopify && window.Shopify.locale) || 'de').toLowerCase().indexOf('de') === 0) ? 'Zur Produktseite' : 'View product page'); /* bAK: sprachabhaengig (Cowork 2026-06-11) */

    // Insert directly AFTER the form/buy buttons (under the ATC button)
    const form = container.querySelector('product-form-component') ||
                 container.querySelector('buy-buttons-component') ||
                 container.querySelector('[class*="buy-buttons"]') ||
                 container.querySelector('form[action*="/cart/add"]');
    if (form) {
      form.insertAdjacentElement('afterend', link);
    }
  }

  function renderThumbs(modal, data) {
    if (!data || !data.all || data.all.length < 2) return;

    const existing = modal.querySelector('.bak-color-thumbs');
    if (existing) existing.remove();

    const row = buildRow(data);

    // Desktop: inside .product-details. Mobile (no .product-details): in modal-content.
    const productDetails = modal.querySelector('.product-details');
    const modalContent = modal.querySelector('#quick-add-modal-content');
    const container = productDetails || modalContent;
    if (!container) return;

    // Place thumbs between price and variant-picker. Strategy:
    // 1) Prefer inserting directly BEFORE the main <variant-picker> — that puts
    //    the row exactly where we want regardless of which container holds the price.
    // 2) Fallback: insert AFTER the main <product-price> (skipping prices inside
    //    recommendations / accordions / product cards which are not the main one).
    // 3) Last resort: prepend to container.
    const isInExcluded = (el) =>
      el.closest('product-recommendations') ||
      el.closest('.accordion') ||
      el.closest('details') ||
      el.closest('product-card') ||
      el.closest('.product-card');

    const pickFirstUsable = (els) => {
      for (const el of els) {
        if (!isInExcluded(el)) return el;
      }
      return els[0] || null;
    };

    const variantPicker = pickFirstUsable(container.querySelectorAll('variant-picker'));
    const priceEl = pickFirstUsable(container.querySelectorAll('product-price'));

    if (variantPicker) {
      variantPicker.insertAdjacentElement('beforebegin', row);
    } else if (priceEl) {
      priceEl.insertAdjacentElement('afterend', row);
    } else {
      container.insertBefore(row, container.firstChild);
    }

    row.querySelectorAll('.bak-color-thumb').forEach((btn) => {
      btn.addEventListener('click', onThumbClick);
    });

    modal.setAttribute('data-bak-current-handle', data.current.handle);
  }

  async function onThumbClick(event) {
    event.preventDefault();
    const btn = event.currentTarget;
    const url = btn.getAttribute('data-product-url');
    const handle = btn.getAttribute('data-product-handle');
    if (!url) return;

    const modal = btn.closest('.quick-add-modal');
    const currentHandle = modal?.getAttribute('data-bak-current-handle');
    if (handle === currentHandle) return;

    btn.setAttribute('data-bak-loading', 'true');

    const anyQa = findActiveQuickAdd();
    if (!anyQa || typeof anyQa.fetchProductPage !== 'function') {
      window.location.href = url;
      return;
    }

    try {
      const html = await anyQa.fetchProductPage(url);
      if (!html) throw new Error('No HTML returned');

      const gridElement =
        html.querySelector('[data-product-grid-content]') ||
        html.querySelector('main .product-information') ||
        html.querySelector('main') ||
        null;

      if (!gridElement) throw new Error('No grid element found in fetched HTML');

      if (typeof anyQa.updateQuickAddModal === 'function') {
        await anyQa.updateQuickAddModal(gridElement.cloneNode(true));
      } else {
        const modalContent = document.getElementById('quick-add-modal-content');
        if (modalContent) {
          modalContent.innerHTML = '';
          modalContent.appendChild(gridElement.cloneNode(true));
        }
      }

      // CRITICAL: re-initialise Horizon's variant-picker for the swapped
      // product. updateQuickAddModal only morphs the DOM — without this the
      // variant-picker keeps the ORIGINAL product's internal state (#radios,
      // checked indices, product url) and add-to-cart resolves the wrong
      // variant (e.g. always XS). Horizon's own modal-open does the same via
      // its private #updateVariantPicker.
      try {
        const modalVP = (document.getElementById('quick-add-modal-content') || modal)
          .querySelector('variant-picker');
        if (modalVP && typeof modalVP.updateVariantPicker === 'function') {
          modalVP.updateVariantPicker(gridElement.cloneNode(true));
          log('variant-picker re-initialised for swapped product');
        }
      } catch (e) {
        console.warn('[bak-color-thumbs] updateVariantPicker failed', e);
      }

      const fetchedQa = html.querySelector('quick-add-component');
      let newData = null;
      if (fetchedQa) {
        const variantsScript = fetchedQa.querySelector('script.bak-color-variants-data');
        const currentScript = fetchedQa.querySelector('script.bak-current-product-data');
        if (variantsScript && currentScript) {
          try {
            const variants = JSON.parse(variantsScript.textContent || '[]');
            const current = JSON.parse(currentScript.textContent || '{}');
            const seen = new Set();
            const all = [current, ...variants].filter((p) => {
              if (!p || !p.handle) return false;
              if (seen.has(p.handle)) return false;
              seen.add(p.handle);
              return true;
            });
            newData = { current, all };
          } catch (e) { /* ignore */ }
        }
      }

      if (!newData) {
        const old = readVariants(anyQa);
        if (old) {
          const newCurrent = old.all.find((p) => p.handle === handle) || old.current;
          newData = { current: newCurrent, all: old.all };
        }
      }

      if (newData) renderThumbs(modal, newData);
      // bAK: nach dem Color-Swap auch den 'Zur Produktseite' Link wieder einfügen
      // (wurde vom updateQuickAddModal morph rausgenommen)
      try { renderProductLink(modal); } catch (e) { console.warn('[bak-color-thumbs] renderProductLink failed after swap', e); }
    } catch (e) {
      console.warn('[bak-color-thumbs] in-modal swap failed, falling back to navigation', e);
      window.location.href = url;
    } finally {
      btn.removeAttribute('data-bak-loading');
    }
  }

  function attachLastTriggeredTracking() {
    document.addEventListener('click', (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const qaBtn = target.closest('.quick-add__button--choose, .quick-add__button--add');
      if (!qaBtn) return;
      const qac = qaBtn.closest('quick-add-component');
      if (!qac) return;
      document.querySelectorAll('quick-add-component[data-bak-last-triggered]').forEach((el) =>
        el.removeAttribute('data-bak-last-triggered')
      );
      qac.setAttribute('data-bak-last-triggered', 'true');
    }, true);
  }

  function log(...args) {
    try { console.log('[bak-color-thumbs]', ...args); } catch(e) {}
  }

  function watchModal() {
    const modal = document.querySelector('.quick-add-modal');
    if (!modal) {
      window.setTimeout(watchModal, 400);
      return;
    }
    log('watchModal initialized');

    // Render ONCE per modal open (attribute change). No polling, no cascading observer.
    let renderTimer = null;
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName === 'open' && modal.hasAttribute('open')) {
          if (renderTimer) clearTimeout(renderTimer);
          // Defer 250ms so the modal content has been fetched + injected
          renderTimer = window.setTimeout(() => {
            try {
              if (!modal.hasAttribute('open')) return;
              const qa = findActiveQuickAdd();
              if (!qa) return;
              const data = readVariants(qa);
              if (data) renderThumbs(modal, data);
              renderProductLink(modal);
            } catch (e) {
              console.warn('[bak-color-thumbs] render failed', e);
            }
          }, 250);
        }
      }
    }).observe(modal, { attributes: true, attributeFilter: ['open'] });
  }




  /**
   * Bulletproof Add-to-Cart für das Quick-Add-Modal.
   *
   * Problem: Nach einem In-Modal-Farbwechsel feuert Horizons variant-picker
   * zwar ein korrektes `variant:update`-Event, aber die product-form-component
   * schreibt die neue Varianten-ID NICHT in ihr verstecktes input[name="id"]
   * (ihre `refs` zeigen nach dem Morph auf ein veraltetes, abgehängtes Input).
   * Das Formular schickt dann die nach dem Swap vorausgewählte Variante ab —
   * die falsche Größe. (Im Browser verifiziert: checked-Radio = M, aber
   * /cart/add.js bekam die XS/L-ID.)
   *
   * Lösung: den Add-to-Cart-Klick im Modal abfangen, Horizons eigenen Submit
   * abbrechen, die Varianten-ID direkt vom angehakten Größen-Radio lesen
   * (server-gerendertes data-variant-id — immer korrekt), selbst per
   * /cart/add.js hinzufügen und Horizons `cart:update`-Event feuern, damit
   * Warenkorb-Drawer + Header aktualisieren und das Modal schließt.
   */
  function attachAddToCartBypass() {
    document.addEventListener(
      'click',
      async function (e) {
        if (!(e.target instanceof Element)) return;
        const btn = e.target.closest('button[name="add"], [ref="addToCartButton"]');
        if (!btn) return;
        const modal = btn.closest('.quick-add-modal');
        if (!modal) return;

        const pfc = btn.closest('product-form-component');
        const scope =
          btn.closest('.product-details') ||
          modal.querySelector('#quick-add-modal-content') ||
          modal;
        const vp =
          [...scope.querySelectorAll('variant-picker')].find(
            (v) => !v.closest('product-recommendations')
          ) || scope.querySelector('variant-picker');
        if (!vp) return;
        // Nur Ein-Options-Produkte (eine Fieldset = Größe). Bei mehreren
        // Optionen lässt sich die Varianten-ID nicht aus einem Radio ableiten
        // — dann Horizon machen lassen.
        if (vp.querySelectorAll('fieldset').length > 1) return;
        const checked = vp.querySelector(
          'input[type="radio"][data-variant-id]:checked'
        );
        if (!checked || !checked.dataset.variantId) return;
        const variantId = checked.dataset.variantId;

        // Komplett übernehmen — Horizons (defekten) Submit unterbinden.
        e.preventDefault();
        e.stopImmediatePropagation();

        if (btn.hasAttribute('data-bak-adding')) return;
        btn.setAttribute('data-bak-adding', 'true');

        try {
          const sectionIds = [...document.querySelectorAll('cart-items-component')]
            .map((c) => c.dataset.sectionId)
            .filter(Boolean);
          const fd = new FormData();
          fd.set('id', variantId);
          fd.set('quantity', '1');
          if (sectionIds.length) fd.set('sections', sectionIds.join(','));

          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/javascript' },
            body: fd,
          });
          const added = await addRes.json();
          if (added && added.status) {
            console.warn('[bak-color-thumbs] add failed', added.message);
            return;
          }
          log('added correct variant', variantId);

          let cart = null;
          try {
            cart = await fetch('/cart.js').then((r) => r.json());
          } catch (e2) {
            /* ignore */
          }

          // Aus dem Modal heraus feuern: bubbelt zur QuickAddDialog (schließt
          // das Modal) und zum Header/Cart-Drawer — exakt Horizons Event.
          const ev = new Event('cart:update', { bubbles: true });
          ev.detail = {
            resource: cart || undefined,
            sourceId: (pfc && pfc.id) || 'bak-quick-add',
            data: {
              source: 'product-form-component',
              itemCount: 1,
              productId: pfc && pfc.dataset.productId,
              sections: added && added.sections,
            },
          };
          (pfc || modal).dispatchEvent(ev);
        } catch (err) {
          console.warn('[bak-color-thumbs] add bypass error', err);
        } finally {
          btn.removeAttribute('data-bak-adding');
        }
      },
      true
    );
  }

  /* bAK: PDP Wishlist-Herz auf das sichtbare Bild positionieren.
     Das Gallery-Element ist auf Desktop breiter als das tatsaechlich sichtbare
     Bild (object-fit: contain, Image-Aspect kleiner als Container-Aspect).
     Die brandeed-wishlist-App setzt right:16 relativ zur Gallery-Breite, was
     das Herz im rechten Whitespace neben dem Bild landen laesst. Dieser Code
     berechnet die echten Bildgrenzen und repositioniert das Herz. */
  let __bakWishlistReposLock = false;
  let __bakWishlistLastApplied = { right: null, top: null };
  function repositionPdpWishlist() {
    const overlay = document.querySelector('.wishlist-overlay--pdp');
    if (!overlay) return;
    const gallery = overlay.parentElement;
    if (!gallery) return;
    const img = gallery.querySelector('img');
    if (!img || !img.naturalWidth) return;

    const galleryRect = gallery.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();
    const naturalAspect = img.naturalWidth / img.naturalHeight;
    const renderedAspect = imgRect.width / imgRect.height;

    let visibleW = imgRect.width;
    let visibleH = imgRect.height;
    let visibleLeftInImg = 0;
    let visibleTopInImg = 0;
    if (naturalAspect < renderedAspect) {
      visibleW = imgRect.height * naturalAspect;
      visibleLeftInImg = (imgRect.width - visibleW) / 2;
    } else if (naturalAspect > renderedAspect) {
      visibleH = imgRect.width / naturalAspect;
      visibleTopInImg = (imgRect.height - visibleH) / 2;
    }

    const visibleRight = imgRect.left + visibleLeftInImg + visibleW;
    const visibleTop = imgRect.top + visibleTopInImg;
    // bAK: Herz rechts oben — 24px Inset zur rechten Bildkante und 24px von oben.
    const rightOffset = Math.round((galleryRect.right - visibleRight) + 24);
    const topOffset = Math.round((visibleTop - galleryRect.top) + 24);

    __bakWishlistReposLock = true;
    overlay.style.setProperty('inset', 'auto', 'important');
    overlay.style.setProperty('left', 'auto', 'important');
    overlay.style.setProperty('bottom', 'auto', 'important');
    overlay.style.setProperty('right', rightOffset + 'px', 'important');
    overlay.style.setProperty('top', topOffset + 'px', 'important');
    __bakWishlistLastApplied = { right: rightOffset, top: topOffset };
    // Release lock on next tick
    setTimeout(() => { __bakWishlistReposLock = false; }, 0);
  }

  function maybeRepositionPdpWishlist() {
    if (__bakWishlistReposLock) return;
    const overlay = document.querySelector('.wishlist-overlay--pdp');
    if (!overlay) return;
    const expectedLeftPx = __bakWishlistLastApplied.left;
    if (expectedLeftPx == null) {
      repositionPdpWishlist();
      return;
    }
    const currentLeftNum = parseFloat(overlay.style.left);
    if (isNaN(currentLeftNum) || Math.abs(currentLeftNum - expectedLeftPx) > 1) {
      repositionPdpWishlist();
    }
  }

  let __bakPdpWishlistInitialized = false;
  function setupPdpWishlistWatchers() {
    if (__bakPdpWishlistInitialized) return;
    const overlay = document.querySelector('.wishlist-overlay--pdp');
    if (!overlay) return;
    __bakPdpWishlistInitialized = true;
    repositionPdpWishlist();
    document.querySelectorAll('.media-gallery__grid img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', repositionPdpWishlist, { once: true });
    });
    window.addEventListener('resize', repositionPdpWishlist);
    const mo = new MutationObserver(maybeRepositionPdpWishlist);
    mo.observe(overlay, { attributes: true, attributeFilter: ['style'] });
    [50, 150, 400, 1000, 2500].forEach((ms) => setTimeout(repositionPdpWishlist, ms));
  }

  function watchPdpWishlist() {
    // Only run on PDP — detect via media-gallery presence (works for both grid + carousel mode).
    const gallery = document.querySelector('media-gallery, .media-gallery__grid, .product-media-constraint-wrapper');
    if (!gallery) return;
    setupPdpWishlistWatchers();
    if (__bakPdpWishlistInitialized) return;
    // Brandeed-wishlist injects the overlay async. Watch the gallery for it.
    const galleryObserver = new MutationObserver(() => {
      setupPdpWishlistWatchers();
      if (__bakPdpWishlistInitialized) galleryObserver.disconnect();
    });
    galleryObserver.observe(gallery, { childList: true, subtree: true });
    // Fallback: try a few times in case observer misses it
    [200, 800, 2000, 4000].forEach((ms) => setTimeout(setupPdpWishlistWatchers, ms));
  }

  function init() {
    attachLastTriggeredTracking();
    attachAddToCartBypass();
    watchModal();
    watchPdpWishlist();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

















